import {
  ReconciliationFormData,
  ValueAdjustmentData,
  DutyRateAdjustmentData,
  CombinedAdjustmentData,
  validateTransformation,
  validateCH99Code,
} from "../types/reconciliation";

// Helper function to normalize CH99 codes to dotted format if needed
export const normalizeCH99Code = (code: string): string => {
  // Return special values as-is
  if (!code || code === 'ANY') return code;
  
  // If it's already in dotted format, return as is
  if (/^99\d{2}\.\d{2}\.\d{2}$/.test(code)) {
    return code;
  }
  
  // If it's 8 digits starting with 99, add dots
  if (/^99\d{6}$/.test(code)) {
    return `${code.slice(0, 4)}.${code.slice(4, 6)}.${code.slice(6, 8)}`;
  }
  
  // Otherwise return as is (including 'ANY' or other special values)
  return code;
};

export const validateReconciliationForm = (
  formData: ReconciliationFormData
): Record<string, string[]> => {
  const errors: Record<string, string[]> = {};

  // Validate date range
  if (!formData.dateRange.start || !formData.dateRange.end) {
    errors.dateRange = ["Date range is required"];
  } else if (
    new Date(formData.dateRange.start) > new Date(formData.dateRange.end)
  ) {
    errors.dateRange = ["Start date must be before end date"];
  }

  // Validate adjustment data based on type
  if (formData.adjustmentType === "value_percentage") {
    const adjustmentData = formData.adjustmentData as ValueAdjustmentData;
    const percentageErrors = validateValueAdjustment(adjustmentData);
    if (percentageErrors.length > 0) {
      errors.percentage = percentageErrors;
    }
  } else if (formData.adjustmentType === "duty_rate_adjustment") {
    const adjustmentData = formData.adjustmentData as DutyRateAdjustmentData;
    const dutyRateErrors = validateDutyRateAdjustment(adjustmentData);
    if (dutyRateErrors.length > 0) {
      errors.dutyRate = dutyRateErrors;
    }
  } else if (formData.adjustmentType === "combined_value_and_duty") {
    const adjustmentData = formData.adjustmentData as CombinedAdjustmentData;
    const combinedErrors = validateCombinedAdjustment(adjustmentData);
    if (combinedErrors.length > 0) {
      errors.combined = combinedErrors;
    }
  }

  // Validate HTS codes format if provided
  if (formData.filters.htsCodes && formData.filters.htsCodes.length > 0) {
    const htsErrors: string[] = [];
    formData.filters.htsCodes.forEach((htsCode) => {
      // Allow wildcards or exact matches
      if (
        !htsCode.includes("*") &&
        !/^[0-9]{8,10}$/.test(htsCode)
      ) {
        htsErrors.push(`Invalid HTS code format: ${htsCode}`);
      }
    });
    if (htsErrors.length > 0) {
      errors.htsCodes = htsErrors;
    }
  }

  return errors;
};

// Validate individual HTS code format (for single input components)
export const validateHtsCode = (htsCode: string): string | null => {
  if (!htsCode || htsCode.trim() === '') {
    return null; // Empty is valid for optional fields
  }
  
  const trimmedCode = htsCode.trim();
  
  // Allow wildcards
  if (trimmedCode.includes("*")) {
    return null; // Valid wildcard
  }
  
  // Check for CH99 format - both with dots (9999.99.99) and without (99999999)
  if (/^99\d{2}\.\d{2}\.\d{2}$/.test(trimmedCode) || /^99\d{6}$/.test(trimmedCode)) {
    return null; // Valid CH99 code
  }
  
  // Check for regular HTS format (8-10 digits)
  if (/^[0-9]{8,10}$/.test(trimmedCode)) {
    return null; // Valid HTS code
  }
  
  return `Invalid code format. Use 8-10 digits, CH99 format (99999999 or 9999.99.99), or wildcards (e.g., 1234*)`;
};

export const validateValueAdjustment = (
  data: ValueAdjustmentData
): string[] => {
  const errors: string[] = [];

  if (!data.adjustmentMode) {
    errors.push("Adjustment mode is required");
    return errors;
  }

  if (data.adjustmentMode === 'percentage') {
    if (!data.percentage) {
      errors.push("Percentage is required");
    } else if (!data.percentage.endsWith("%")) {
      errors.push("Percentage must include % symbol (e.g., '5%')");
    } else {
      const numValue = parseFloat(data.percentage.replace("%", ""));
      if (isNaN(numValue)) {
        errors.push("Invalid percentage format");
      }
    }
  } else if (data.adjustmentMode === 'amount') {
    if (data.amount === undefined || data.amount === null) {
      errors.push("Amount is required");
    } else if (typeof data.amount !== 'number' || isNaN(data.amount)) {
      errors.push("Invalid amount format");
    } else if (data.amount === 0) {
      errors.push("Amount cannot be zero");
    }

    if (!data.currency) {
      errors.push("Currency is required when using amount adjustment");
    }
  }

  return errors;
};

export const validateDutyRateAdjustment = (
  data: DutyRateAdjustmentData
): string[] => {
  const errors: string[] = [];

  if (!data.adjustments || data.adjustments.length === 0) {
    errors.push("At least one adjustment is required");
  } else {
    data.adjustments.forEach((adjustment, index) => {
      // Validate operation type
      if (!adjustment.operation || !['code_change', 'rate_adjustment'].includes(adjustment.operation)) {
        errors.push(`Invalid operation type at entry ${index + 1}`);
        return;
      }

      if (adjustment.operation === 'code_change') {
        // Validate code change fields
        if (!adjustment.originalCode) {
          errors.push(`Original code is required for code change at entry ${index + 1}`);
        } else {
          const codeError = validateHtsCode(adjustment.originalCode);
          if (codeError) {
            errors.push(`Original code at entry ${index + 1}: ${codeError}`);
          }
        }

        if (!adjustment.targetCode) {
          errors.push(`Target code is required for code change at entry ${index + 1}`);
        } else {
          const codeError = validateHtsCode(adjustment.targetCode);
          if (codeError) {
            errors.push(`Target code at entry ${index + 1}: ${codeError}`);
          }
        }

        if (adjustment.originalCode && adjustment.targetCode && adjustment.originalCode === adjustment.targetCode) {
          errors.push(`Original and target codes cannot be the same at entry ${index + 1}`);
        }

        // Validate CH99 change mode if present
        if (adjustment.ch99ChangeMode) {
          if (adjustment.ch99ChangeMode === 'replace_all') {
            // Smart validation: differentiate between "not set" (undefined) and "empty" ([])
            // undefined = user just selected mode, hasn't interacted yet - don't validate
            // [] or empty array = user is in the mode but hasn't added data - validate and show error
            if (adjustment.newCH99CodeList === undefined) {
              // Mode just selected, arrays not initialized yet - skip validation for better UX
              // This prevents immediate validation errors when user selects a mode
            } else if (adjustment.newCH99CodeList.length === 0) {
              errors.push(`Entry ${index + 1}: Replace-all mode requires at least one CH99 code. Click the dropdown to add codes.`);
            } else {
              // Validate each code in the list
              adjustment.newCH99CodeList.forEach((code, codeIndex) => {
                const codeError = validateCH99Code(code);
                if (codeError) {
                  errors.push(`Entry ${index + 1}, CH99 code ${codeIndex + 1}: ${codeError}`);
                }
              });
            }
          } else if (adjustment.ch99ChangeMode === 'selective') {
            // Smart validation: differentiate between "not set" (undefined) and "empty" ([])
            if (adjustment.ch99Transformations === undefined) {
              // Mode just selected, arrays not initialized yet - skip validation for better UX
              // This prevents immediate validation errors when user selects a mode
            } else if (adjustment.ch99Transformations.length === 0) {
              errors.push(`Entry ${index + 1}: Selective mode requires at least one transformation. Click "+ Add Transformation" to create one.`);
            } else {
              // Validate each transformation
              adjustment.ch99Transformations.forEach((transform, transformIndex) => {
                const transformErrors = validateTransformation(transform);
                transformErrors.forEach(error => {
                  errors.push(`Entry ${index + 1}, Transformation ${transformIndex + 1}: ${error}`);
                });
              });
            }
          }
        }

      } else if (adjustment.operation === 'rate_adjustment') {
        // Validate rate adjustment fields
        if (!adjustment.htsCode) {
          errors.push(`HTS code is required for rate adjustment at entry ${index + 1}`);
        } else {
          const codeError = validateHtsCode(adjustment.htsCode);
          if (codeError) {
            errors.push(`HTS code at entry ${index + 1}: ${codeError}`);
          }
        }

        if (typeof adjustment.newRate !== 'number' || isNaN(adjustment.newRate)) {
          errors.push(`Valid new rate is required for rate adjustment at entry ${index + 1}`);
        } else if (adjustment.newRate < 0 || adjustment.newRate > 10) {
          errors.push(`Rate at entry ${index + 1} must be between 0 and 10 (0% to 1000%)`);
        }
      }
    });

    // Check maximum entries
    if (data.adjustments.length > 1000) {
      errors.push("Maximum 1000 adjustments allowed per request");
    }
  }

  return errors;
};

export const validateCombinedAdjustment = (
  data: CombinedAdjustmentData
): string[] => {
  const errors: string[] = [];

  // Validate value adjustment part
  if (!data.valueAdjustment || !data.valueAdjustment.percentage) {
    errors.push("Value adjustment percentage is required for combined adjustment");
  } else {
    if (!data.valueAdjustment.percentage.endsWith("%")) {
      errors.push("Percentage must include % symbol (e.g., '5%')");
    } else {
      const numValue = parseFloat(data.valueAdjustment.percentage.replace("%", ""));
      if (isNaN(numValue)) {
        errors.push("Invalid percentage format");
      }
    }
  }

  // Validate duty rate adjustment part
  if (!data.dutyRateAdjustment || !data.dutyRateAdjustment.adjustments) {
    // Duty adjustments are optional for combined type
  } else if (data.dutyRateAdjustment.adjustments.length > 0) {
    // Use existing duty rate validation logic
    const dutyErrors = validateDutyRateAdjustment(data.dutyRateAdjustment);
    dutyErrors.forEach(error => errors.push(`Duty adjustment: ${error}`));
  }

  return errors;
};